/**
 * Validation utilities for the Visual IDF Object Editor
 *
 * Provides real-time validation of IDF objects based on IDD rules.
 */

import type { IDFDocument, IDFObject, IDFField } from '../parser/types';
import type { IDDSchema, IDDObjectType, IDDField } from '../idd/types';
import type {
  ObjectValidation,
  FieldValidation,
  ValidationError,
  ValidationWarning,
  ValidationStatus,
} from './types';

/**
 * Validate a single field against its IDD definition
 */
export function validateField(
  field: IDFField | undefined,
  fieldInfo: IDDField,
  document: IDFDocument | null,
  schema: IDDSchema | null
): FieldValidation {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  const value = field?.value?.toString().trim() ?? '';

  // Required field validation
  if (fieldInfo.required && !value) {
    const error: ValidationError = {
      type: 'required',
      message: `${fieldInfo.name || fieldInfo.id} is required`,
    };
    if (fieldInfo.default) {
      error.suggestion = `Consider using default value: ${fieldInfo.default}`;
    }
    errors.push(error);
  }

  // Skip further validation if field is empty and not required
  if (!value) {
    return {
      status: errors.length > 0 ? 'error' : 'valid',
      errors,
      warnings,
    };
  }

  // Type validation
  if (fieldInfo.type === 'real') {
    const lowerValue = value.toLowerCase();
    if (
      lowerValue !== 'autocalculate' &&
      lowerValue !== 'autosize' &&
      isNaN(parseFloat(value))
    ) {
      errors.push({
        type: 'type',
        message: 'Expected a number',
        suggestion: `Enter a numeric value${fieldInfo.units ? ` in ${fieldInfo.units}` : ''}`,
      });
    }
  }

  if (fieldInfo.type === 'integer') {
    const lowerValue = value.toLowerCase();
    if (
      lowerValue !== 'autocalculate' &&
      lowerValue !== 'autosize'
    ) {
      const num = parseFloat(value);
      if (isNaN(num) || !Number.isInteger(num)) {
        errors.push({
          type: 'type',
          message: 'Expected an integer',
          suggestion: 'Enter a whole number without decimals',
        });
      }
    }
  }

  // Range validation for numeric fields
  if (
    (fieldInfo.type === 'real' || fieldInfo.type === 'integer') &&
    value.toLowerCase() !== 'autocalculate' &&
    value.toLowerCase() !== 'autosize'
  ) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (fieldInfo.minimum !== undefined) {
        const isValid = fieldInfo.exclusiveMinimum
          ? numValue > fieldInfo.minimum
          : numValue >= fieldInfo.minimum;
        if (!isValid) {
          warnings.push({
            type: 'out-of-range',
            message: `Value ${numValue} is below minimum ${fieldInfo.minimum}`,
            suggestion: `Enter a value ${fieldInfo.exclusiveMinimum ? 'greater than' : 'at least'} ${fieldInfo.minimum}`,
          });
        }
      }
      if (fieldInfo.maximum !== undefined) {
        const isValid = fieldInfo.exclusiveMaximum
          ? numValue < fieldInfo.maximum
          : numValue <= fieldInfo.maximum;
        if (!isValid) {
          warnings.push({
            type: 'out-of-range',
            message: `Value ${numValue} is above maximum ${fieldInfo.maximum}`,
            suggestion: `Enter a value ${fieldInfo.exclusiveMaximum ? 'less than' : 'at most'} ${fieldInfo.maximum}`,
          });
        }
      }
    }
  }

  // Choice validation
  if (fieldInfo.choices && fieldInfo.choices.length > 0) {
    const normalizedValue = value.toLowerCase();
    const validChoice = fieldInfo.choices.some(
      (c) => c.toLowerCase() === normalizedValue
    );
    // Also allow autocalculate/autosize if the field supports it
    const allowsAuto =
      (fieldInfo.autocalculatable && normalizedValue === 'autocalculate') ||
      (fieldInfo.autosizable && normalizedValue === 'autosize');
    if (!validChoice && !allowsAuto) {
      errors.push({
        type: 'choice',
        message: `Invalid choice "${value}"`,
        suggestion: `Valid options: ${fieldInfo.choices.slice(0, 5).join(', ')}${fieldInfo.choices.length > 5 ? '...' : ''}`,
      });
    }
  }

  // Reference validation (check if referenced object exists)
  if (fieldInfo.objectList && document && schema) {
    const referencedTypes = schema.referenceLists.get(fieldInfo.objectList);
    if (referencedTypes) {
      const exists = document.objects.some((obj) => {
        // Check if object class is in the reference list
        const classMatches =
          referencedTypes.has(obj.className) ||
          referencedTypes.has(obj.className.toLowerCase());
        if (!classMatches) return false;
        // Check if object name matches
        const objName = obj.fields[0]?.value?.toLowerCase() ?? '';
        return objName === value.toLowerCase();
      });
      if (!exists && value) {
        warnings.push({
          type: 'empty-reference',
          message: `Referenced object "${value}" not found`,
          suggestion: 'Create the referenced object or select an existing one',
        });
      }
    }
  }

  // Deprecated field warning
  if (fieldInfo.deprecated) {
    warnings.push({
      type: 'deprecated',
      message: 'This field is deprecated',
      suggestion: 'Consider removing or updating this field',
    });
  }

  return {
    status: errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid',
    errors,
    warnings,
  };
}

/**
 * Validate an entire object against its IDD definition
 */
export function validateObject(
  object: IDFObject,
  classInfo: IDDObjectType | undefined,
  document: IDFDocument | null,
  schema: IDDSchema | null
): ObjectValidation {
  if (!classInfo) {
    return {
      status: 'warning',
      errors: [],
      warnings: [
        {
          type: 'unusual',
          message: `Unknown object class: ${object.className}`,
          suggestion: 'This object type is not in the IDD schema',
        },
      ],
      fields: [],
    };
  }

  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const fieldValidations: FieldValidation[] = [];

  // Validate each field
  for (let i = 0; i < classInfo.fields.length; i++) {
    const fieldInfo = classInfo.fields[i];
    if (!fieldInfo) {
      fieldValidations.push({ status: 'valid', errors: [], warnings: [] });
      continue;
    }
    const field = object.fields[i];
    const fieldValidation = validateField(field, fieldInfo, document, schema);
    fieldValidations.push(fieldValidation);

    errors.push(...fieldValidation.errors);
    warnings.push(...fieldValidation.warnings);
  }

  // Add empty field validations for extra fields
  for (let i = classInfo.fields.length; i < object.fields.length; i++) {
    fieldValidations.push({ status: 'valid', errors: [], warnings: [] });
  }

  // Check for unique name constraints (first field is usually the name)
  if (
    classInfo.fields[0]?.references &&
    classInfo.fields[0].references.length > 0
  ) {
    const name = object.fields[0]?.value?.toString().toLowerCase();
    if (name && document) {
      const duplicates = document.objects.filter(
        (o) =>
          o.className.toLowerCase() === object.className.toLowerCase() &&
          o.fields[0]?.value?.toLowerCase() === name &&
          o.id !== object.id
      );
      if (duplicates.length > 0) {
        errors.push({
          type: 'unique',
          message: `Duplicate name "${object.fields[0]?.value}" in ${object.className}`,
          suggestion: 'Each object of this type must have a unique name',
        });
      }
    }
  }

  // Check minimum fields requirement
  const filledFields = (object.fields ?? []).filter((f) => f.value.trim() !== '').length;
  if (filledFields < classInfo.minFields) {
    warnings.push({
      type: 'unusual',
      message: `Object has ${filledFields} fields, minimum is ${classInfo.minFields}`,
      suggestion: 'Fill in the required minimum number of fields',
    });
  }

  // Obsolete object warning
  if (classInfo.isObsolete) {
    warnings.push({
      type: 'deprecated',
      message: `${object.className} is obsolete`,
      suggestion: 'Consider using a newer alternative',
    });
  }

  return {
    status: errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid',
    errors,
    warnings,
    fields: fieldValidations,
  };
}

/**
 * Get validation status indicator text
 */
export function getValidationStatusText(status: ValidationStatus): string {
  switch (status) {
    case 'valid':
      return 'Valid';
    case 'warning':
      return 'Warning';
    case 'error':
      return 'Error';
  }
}

/**
 * Find all objects that reference a given object name
 */
export function findReferencingObjects(
  objectName: string,
  document: IDFDocument,
  schema: IDDSchema
): { object: IDFObject; fieldIndex: number; fieldInfo: IDDField }[] {
  const references: { object: IDFObject; fieldIndex: number; fieldInfo: IDDField }[] = [];
  const lowerName = objectName.toLowerCase();

  for (const obj of document.objects) {
    const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
    if (!classInfo) continue;

    for (let i = 0; i < obj.fields.length; i++) {
      const field = obj.fields[i];
      const fieldInfo = classInfo.fields[i];
      if (!field || !fieldInfo || !fieldInfo.objectList) continue;

      if (field.value.toLowerCase() === lowerName) {
        references.push({ object: obj, fieldIndex: i, fieldInfo });
      }
    }
  }

  return references;
}

/**
 * Find all objects that a given object references
 */
export function findReferencedObjects(
  object: IDFObject,
  document: IDFDocument,
  schema: IDDSchema
): { fieldIndex: number; referencedObject: IDFObject }[] {
  const referenced: { fieldIndex: number; referencedObject: IDFObject }[] = [];
  const classInfo = schema.objectTypes.get(object.className.toLowerCase());
  if (!classInfo) return referenced;

  for (let i = 0; i < object.fields.length; i++) {
    const field = object.fields[i];
    const fieldInfo = classInfo.fields[i];
    if (!field || !fieldInfo || !fieldInfo.objectList || !field.value.trim()) continue;

    const referencedObj = document.objects.find(
      (o) => o.fields[0]?.value?.toLowerCase() === field.value.toLowerCase()
    );
    if (referencedObj) {
      referenced.push({ fieldIndex: i, referencedObject: referencedObj });
    }
  }

  return referenced;
}

/**
 * Find node connections for a given node name
 */
export function findNodeConnections(
  nodeName: string,
  document: IDFDocument,
  schema: IDDSchema
): { object: IDFObject; fieldIndex: number; fieldInfo: IDDField }[] {
  const connections: { object: IDFObject; fieldIndex: number; fieldInfo: IDDField }[] = [];
  const lowerName = nodeName.toLowerCase();

  for (const obj of document.objects) {
    const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
    if (!classInfo) continue;

    for (let i = 0; i < obj.fields.length; i++) {
      const field = obj.fields[i];
      const fieldInfo = classInfo.fields[i];
      if (!field || !fieldInfo || fieldInfo.type !== 'node') continue;

      if (field.value.toLowerCase() === lowerName) {
        connections.push({ object: obj, fieldIndex: i, fieldInfo });
      }
    }
  }

  return connections;
}

/**
 * Calculate validation summary for the entire document
 */
export function calculateValidationSummary(
  document: IDFDocument,
  schema: IDDSchema
): { valid: number; warnings: number; errors: number } {
  let valid = 0;
  let warnings = 0;
  let errors = 0;

  for (const obj of document.objects) {
    const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
    const validation = validateObject(obj, classInfo, document, schema);

    switch (validation.status) {
      case 'valid':
        valid++;
        break;
      case 'warning':
        warnings++;
        break;
      case 'error':
        errors++;
        break;
    }
  }

  return { valid, warnings, errors };
}
